import { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { fetchConsents } from '../../store/actions/consent';
import { addOperationalLog, fetchOperationalLogs } from '../../store/actions/operationalLog';
import { Loader } from '../../utils/tools';
import { Form, Row, Col, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { FACILITY_TYPE_OPTIONS } from '../../utils/constants/facilityTypes';
import { formValues, validation, LOADING_CODE_OPTIONS } from './validationSchema';

const AddOperationalLog = ({ onClose }) => {
    const dispatch = useDispatch();
    const currentBranch = useSelector(state => state.branch.current);
    const consents = useSelector(state => state.consent.data);
    const consentsLoading = useSelector(state => state.consent.loading);

    useEffect(() => {
        dispatch(fetchConsents());
    }, [dispatch]);

    const formik = useFormik({
        initialValues: { ...formValues, branchId: currentBranch ?? '' },
        validationSchema: validation,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            dispatch(addOperationalLog({ values }))
                .unwrap()
                .then(() => {
                    dispatch(fetchOperationalLogs({ branchId: currentBranch }));
                    resetForm();
                    onClose();
                })
                .catch(() => {})
                .finally(() => setSubmitting(false));
        },
    });

    const availableConsents = useMemo(() => {
        if (!Array.isArray(consents)) return [];
        return consents.filter((consent) =>
            consent.scope?.type === 'tenant' || consent.scope?.branch?._id === currentBranch
        );
    }, [consents, currentBranch]);

    if (consentsLoading) return <Loader />;

    return (
        <Form onSubmit={formik.handleSubmit}>
            <h2>Pridať prevádzkový denník</h2>
            <hr />

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formName">
                        <Form.Label>Názov denníka *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Názov denníka"
                            {...formik.getFieldProps('name')}
                            isInvalid={formik.touched.name && !!formik.errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formFacilityType">
                        <Form.Label>Typ zariadenia *</Form.Label>
                        <Form.Select
                            aria-label="Vyberte typ zariadenia"
                            {...formik.getFieldProps('facilityType')}
                            isInvalid={formik.touched.facilityType && !!formik.errors.facilityType}
                        >
                            <option value="">Vyberte typ zariadenia:</option>
                            {FACILITY_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.facilityType}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formLoadingCode">
                        <Form.Label>Kód nakladania *</Form.Label>
                        <Form.Select
                            aria-label="Vyberte kód nakladania"
                            {...formik.getFieldProps('loadingCode')}
                            isInvalid={formik.touched.loadingCode && !!formik.errors.loadingCode}
                        >
                            <option value="">Vyberte kód nakladania:</option>
                            {LOADING_CODE_OPTIONS.map((group) => (
                                <optgroup key={group.label} label={group.label}>
                                    {group.options.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.loadingCode}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formConsent">
                        <Form.Label>Súhlas *</Form.Label>
                        <Form.Select
                            aria-label="Vyberte súhlas"
                            {...formik.getFieldProps('consentId')}
                            isInvalid={formik.touched.consentId && !!formik.errors.consentId}
                        >
                            <option value="">Vyberte súhlas:</option>
                            {availableConsents.map((consent) => (
                                <option key={consent._id} value={consent._id}>
                                    {consent.name} ({consent.decisionNumber})
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.consentId}</Form.Control.Feedback>
                        {availableConsents.length === 0 && (
                            <Form.Text className="text-muted">Pre túto prevádzku nie je evidovaný žiadny platný súhlas.</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Predvolené časové využitie zariadenia</Form.Label>
                        <Row className="g-2 align-items-start">
                            <Col xs={5} sm={4} md={3}>
                                <Form.Control
                                    type="time"
                                    aria-label="Predvolené časové využitie od"
                                    {...formik.getFieldProps('defaultOperatingHoursFrom')}
                                    isInvalid={formik.touched.defaultOperatingHoursFrom && !!formik.errors.defaultOperatingHoursFrom}
                                />
                                <Form.Control.Feedback type="invalid">{formik.errors.defaultOperatingHoursFrom}</Form.Control.Feedback>
                            </Col>
                            <Col xs="auto" className="d-flex justify-content-center text-muted" style={{ paddingTop: '7px' }}>
                                –
                            </Col>
                            <Col xs={5} sm={4} md={3}>
                                <Form.Control
                                    type="time"
                                    aria-label="Predvolené časové využitie do"
                                    {...formik.getFieldProps('defaultOperatingHoursTo')}
                                    isInvalid={formik.touched.defaultOperatingHoursTo && !!formik.errors.defaultOperatingHoursTo}
                                />
                                <Form.Control.Feedback type="invalid">{formik.errors.defaultOperatingHoursTo}</Form.Control.Feedback>
                            </Col>
                        </Row>
                        <Form.Text className="text-muted d-flex align-items-center gap-1 mt-1">
                            <FaInfoCircle />
                            <em>Predvyplní sa v nových denných záznamoch, dá sa zmeniť.</em>
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Zodpovední zamestnanci</Form.Label>
                        {formik.values.responsiblePersons.map((person, index) => (
                            <div key={index} className="d-flex align-items-center gap-2 mb-2">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Nastaviť ako predvoleného</Tooltip>}>
                                    <Form.Check
                                        type="checkbox"
                                        className="flex-shrink-0"
                                        checked={!!person?.trim() && formik.values.defaultResponsiblePersons.includes(person)}
                                        onChange={(e) => {
                                            const next = e.target.checked
                                                ? [...formik.values.defaultResponsiblePersons, person]
                                                : formik.values.defaultResponsiblePersons.filter((p) => p !== person);
                                            formik.setFieldValue('defaultResponsiblePersons', next);
                                        }}
                                    />
                                </OverlayTrigger>
                                <Form.Control
                                    type="text"
                                    placeholder="Meno a priezvisko"
                                    value={person}
                                    onChange={(e) => {
                                        const next = [...formik.values.responsiblePersons];
                                        next[index] = e.target.value;
                                        formik.setFieldValue('responsiblePersons', next);
                                        if (formik.values.defaultResponsiblePersons.includes(person) && person?.trim()) {
                                            formik.setFieldValue(
                                                'defaultResponsiblePersons',
                                                formik.values.defaultResponsiblePersons.map((p) => (p === person ? e.target.value : p))
                                            );
                                        }
                                    }}
                                />
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    type="button"
                                    className="flex-shrink-0"
                                    onClick={() => {
                                        formik.setFieldValue('responsiblePersons', formik.values.responsiblePersons.filter((_, i) => i !== index));
                                        formik.setFieldValue('defaultResponsiblePersons', formik.values.defaultResponsiblePersons.filter((p) => p !== person));
                                    }}
                                >
                                    Vymazať
                                </Button>
                            </div>
                        ))}
                        <Button
                            variant="outline-primary"
                            size="sm"
                            type="button"
                            onClick={() => formik.setFieldValue('responsiblePersons', [...formik.values.responsiblePersons, ''])}
                        >
                            + Pridať zamestnanca
                        </Button>
                        <Form.Text className="text-muted d-flex align-items-center gap-1 mt-2">
                            <FaInfoCircle />
                            <em>Zvolení predvolení zamestnanci sa predvyplnia v nových denných záznamoch, dá sa zmeniť.</em>
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-auto">
                <hr />
                <Col className="d-flex justify-content-end">
                    <Button
                        className="button-custom-large"
                        variant="primary"
                        type="submit"
                        size="lg"
                        style={{ marginTop: 'auto' }}
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? <Loader /> : 'Pridať denník'}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default AddOperationalLog;
